import {
  downloadDropboxFile,
  DropboxFile,
  listFolder,
  listFolderRecursive,
} from 'helpers/dropbox';
import {
  FileFetcher,
  ImportPlan,
  ImportPlanAlbum as GenericPlanAlbum,
} from 'helpers/import-types';

export type DropboxPlanAlbum = GenericPlanAlbum<DropboxFile, DropboxFile>;

export type DropboxPlan = ImportPlan<DropboxFile, DropboxFile>;

/**
 * Scan a Dropbox folder one level deep: each immediate subfolder becomes an
 * album whose memories are all importable files under it (recursive), and
 * importable files directly in the folder come back as unsorted.
 */
export const planDropboxImport = async (
  rootFolder: DropboxFile,
  isCancelled?: () => boolean,
): Promise<DropboxPlan> => {
  const subfolders: DropboxFile[] = [];
  const unsorted: DropboxFile[] = [];
  let cursor: string | undefined;
  do {
    // eslint-disable-next-line no-await-in-loop
    const page = await listFolder(rootFolder.pathLower, cursor);
    page.files.forEach((f) => {
      if (f.isFolder) subfolders.push(f);
      else if (f.isImportable) unsorted.push(f);
    });
    cursor = page.cursor;
  } while (cursor && !isCancelled?.());

  const planned: DropboxPlanAlbum[] = [];
  for (let i = 0; i < subfolders.length; i += 1) {
    if (isCancelled?.()) break;
    const folder = subfolders[i];
    // eslint-disable-next-line no-await-in-loop
    const files = await listFolderRecursive(folder.pathLower);
    if (files.length > 0) {
      planned.push({ folder, files });
    }
  }

  return { rootName: rootFolder.name, albums: planned, unsorted };
};

export const fetchDropboxFile: FileFetcher<DropboxFile> = async (
  dropboxFile,
) => {
  const downloaded = await downloadDropboxFile(dropboxFile);
  const lastModified = downloaded.modifiedTime
    ? Date.parse(downloaded.modifiedTime)
    : Date.now();
  return new File([downloaded.blob], downloaded.name, {
    type: downloaded.mimeType || 'application/octet-stream',
    lastModified: Number.isFinite(lastModified) ? lastModified : Date.now(),
  });
};
